
import './CardFinderResults.css';
import React, { PropsWithChildren, useEffect, useState } from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';
import { TCreditCard } from '../../types/models/CreditCard';
import axios from 'axios';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';
import { ButtonWrapper, LinkWrapper } from '../LinkWrapper/LinkWrapper';
import { fetchBatchBVData, StarArray, IReviewData } from '../../foundation/StarArray/StarArray';
import { useWidthContext } from '../../providers/Width';
import { List, ToolTip, CardContainer, Image } from '../../importCL';
interface ICardFinderResultsProps extends MappedComponentProperties {
    reviewTooltipText?: string;
    bulletPointIconPath?: IIcon;
}

interface IIcon {
    iconLabel: string;
    iconPath: string;
    iconAltText: string;
}

export const CardFinderResultsEditConfig = {
    emptyLabel: 'Card Finder Results',
    isEmpty: (props: PropsWithChildren<ICardFinderResultsProps>) => {
        return !props || !props.bulletPointIconPath;
    },
};

const getCardsParam = (searchParams: string) => {
    if (!searchParams) {
        return '';
    }
    searchParams = searchParams.replace('?wcmmode=disabled', '');
    const params = new URLSearchParams(searchParams);
    return params.get('cards') ? '.cards=' + params.get('cards') : '';
};

const CardFinderResults = (props: PropsWithChildren<ICardFinderResultsProps>): JSX.Element => {
    const url: string = '/services/cardFinderResults' + getCardsParam(window.location.search) + '.json';
    const { width } = useWidthContext();
    const [cardFinderCards, setCardFinderCards] = useState<TCreditCard[]>([]);
    const [reviewData, setData] = useState<IReviewData[]>([{ productId: '', average: 0, total: 0, overall: 0 }]);

    const handleCardFinderServletResults = (cards: TCreditCard[]) => {
        const resultCards: TCreditCard[] = [];
        cards.forEach((cardData: TCreditCard) => resultCards.push(cardData));
        setCardFinderCards(resultCards);
    };

    const cardAnnualFeeAdjustment = (annualFeeText: string) => {
        const annualFeeRegex = /\$[0-9]*[.]?[0-9]*/gi;
        const annualFeeRegexResult = annualFeeText.match(annualFeeRegex);
        const hasExcellentOption = annualFeeText.includes('Excellent credit');
        const hasAverageOption = annualFeeText.includes('Average credit');
        const hasRebuildingOption = annualFeeText.includes('Rebuilding credit') || annualFeeText.includes('first year');
        const annualFee = [];

        if (annualFeeRegexResult.length < 2) {
            return (transformHTMLParserLinks('<b>' + annualFeeRegexResult[0] + '</b>'));
        }


        //check for the range of the annual fee between options (not including the monthly one) and display as a range
        const annualFeeIntegers = [];
        let annualFeeRange = '';
        
        annualFeeRegexResult.forEach((fee) => {
            annualFeeIntegers.push(Number(fee.substring(1))); 
        });

        if (annualFeeIntegers[annualFeeIntegers.length - 1] === 8.25) {
            annualFeeRange = '<b>' + annualFeeRegexResult[0] + ' - ' + annualFeeRegexResult[annualFeeIntegers.length - 2] + '</b>';
        } else {
            annualFeeRange = '<b>' + annualFeeRegexResult[0] + ' - ' + annualFeeRegexResult[annualFeeIntegers.length - 1] + '</b>';
        }


        //excellent credit
        if (hasExcellentOption) {
            annualFee.push('Excellent credit : ' + annualFeeRegexResult[0]);
        }

        //average credit
        if (hasAverageOption) {
            if (hasExcellentOption) { 
                annualFee.push('Average credit : ' + annualFeeRegexResult[1]);
            } else {
                annualFee.push('Average credit : ' + annualFeeRegexResult[0]);
            }
            
        }

        //rebuilding credit
        if (hasRebuildingOption) { 
            const firstYear = annualFeeRegexResult[annualFeeRegexResult.length - 3];
            const followingYears = annualFeeRegexResult[annualFeeRegexResult.length - 2];
            const monthlyCharge = annualFeeRegexResult[annualFeeRegexResult.length - 1];
            if (annualFeeRegexResult.length === 3) {
                annualFee.push(firstYear + ' first year, then ' + followingYears + ' annually (billed at ' + monthlyCharge + ' per month)');
            } else {
                annualFee.push('Rebuilding credit : ' + firstYear + ' first year, then ' + followingYears + ' annually (billed at ' + monthlyCharge + ' per month)');
            }
        }

        return (
            <>
                {transformHTMLParserLinks(annualFeeRange)}
                <ul>
                    {annualFee.map((feeItem, index) => {
                        return (
                            <li key={index}>
                                {feeItem}
                            </li>
                        );
                    })}
                </ul>
            </>
        );
    };

    const cleanCardProductName = (productName: string) => {
        const productNameBreakIndex = productName.indexOf('<br>');

        if (productNameBreakIndex !== -1) {
            const cleanedProductName = productName.substring(0, productNameBreakIndex) + ' ' + productName.substring(productNameBreakIndex + 4);
            return (transformHTMLParserLinks(cleanedProductName));
        }
        
        return transformHTMLParserLinks(productName);
    };

    const cardHeading = (index: number, card: TCreditCard) => {
        return (
            <div className='flex flex-col'>
                <div className='font-semibold pb-5'>{(index+1) + ' of ' + cardFinderCards.length}</div>
                <div className='text-grey-tundora font-bold text-2xl md:text-xl leading-7 pb-2'>{cleanCardProductName(card.productName)}</div>
                <div className='text-grey-tundora font-normal text-base md:text-s leading-6'>{transformHTMLParserLinks(card.productDescription)}</div>
            </div>
        );
    };

    const cardDescription = (card: TCreditCard) => {
        return (
            <div className='flex flex-col'>
                <div className='flex flex-row'>
                    <div className='CardFinderResults__recommendedListTitle text-grey-tundora'>Recommended for:</div>
                    <List className='!pl-0 !mt-0' items={card.recommendedFeatures} checks/>
                </div>

                <hr></hr>

                <div className='flex flex-row'>
                    <div className='CardFinderResults__cardAPRTitle'>{transformHTMLParserLinks(card.aprTitle)}</div>
                    <p>{transformHTMLParserLinks(card.aprLow) + '% ' + card.aprText}</p>
                </div>

                <hr></hr>

                <div className='flex flex-row'>
                    <div className='CardFinderResults__cardAnnualFeeTitle'>{transformHTMLParserLinks(card.annualFeeTitle)}</div>
                    <div className='CardFinderResults__cardAnnualFeeDescription text-s self-center'>{cardAnnualFeeAdjustment(card.annualFeeText)}</div>
                </div>
            </div>
        );
    };

    const cardImage = (card: TCreditCard) => {
        return (
            <Image
                className='md:w-[55%]'
                loading='lazy'
                alt={card.cardImageAltText}
                src={card.imagePath} />
        );
    };

    const cardReviews = (card: TCreditCard) => {
        if (card.bazaarvoiceID) {
            const cardData = reviewData.find((d) => d.productId === card.bazaarvoiceID);
            return (
                <div className='CardFinderResults__cardReviews'>
                    <a href={card.bazaarvoiceStarsLink}
                        target={card.bazaarvoiceStarsLinkTarget}>
                        <StarArray size={ width > 769 ? 24 : 18}
                            average={cardData?.average ?? 0}
                            total={cardData?.total ?? 0}
                            overall={cardData?.overall ?? 0} />
                    </a>
                    {(cardData?.average ?? 0).toFixed(1)} out of {cardData?.overall ?? 0} stars
                    <br />
                    <span>based on</span>
                    <a className='no-underline' href={card.bazaarvoiceStarsLink}
                        target={card.bazaarvoiceStarsLinkTarget}>
                        {` ${(cardData?.total ?? 0).toLocaleString('en-US')} Reviews `}
                    </a>
                    {props.reviewTooltipText && 
                        <ToolTip
                            tabIndex={0}
                            mobileNotchPosition='top-right'
                            notchPosition='top-right'>
                            {transformHTMLParserLinks(props.reviewTooltipText)}
                        </ToolTip>
                    }
                </div>);
        }
    };

    const cardCTA = (card: TCreditCard) => {
        return (
            <>
                <ButtonWrapper size='medium'
                    href={card.preQualifyLink}
                    variant='solid'
                    className='md:self-center w-[80%] md:max-w-60 mt-8 md:mt-0'>
                    Apply Now
                </ButtonWrapper>
                <LinkWrapper className='no-underline text-center' href={card.dynamicTermsLink ?? card.staticLink}>{transformHTMLParserLinks(card.aprDisclaimerText)}</LinkWrapper>
            </>
        );
    };

    useEffect(() => {
        axios.get(url)
            .then((response) => {
                handleCardFinderServletResults(response.data.cards);
            })
            // eslint-disable-next-line no-console
            .catch((err) => console.log(err));
    }, []);

    useEffect(() => {
        const bazaarVoiceIDList = [];
        cardFinderCards.forEach((card) => {
            if (card.bazaarvoiceID) {
                bazaarVoiceIDList.push(card.bazaarvoiceID);
            }
            if (card.aprLow === '0.0' || card.aprLow === '') {
                window.location.assign('/404');
            }
        });
        
        fetchBatchBVData(bazaarVoiceIDList).then(setData);
    }, [cardFinderCards]);

    return (
        <>
            <div id='card-finder-results' className='w-full'>
                <div className='bg-grey-alabaster ml-[-100%] mr-[-100%] pt-16 pb-28'>
                    <h3 className='flex justify-center text-4xl md:text-3xl leading-12'>Thank you!</h3>
                    <div className='justify-self-center text-center font-normal text-2xl md:text-lg ml-[35%] mr-[35%]'>
                        {cardFinderCards.length > 1 ? `Based on your responses, we recommend these ${cardFinderCards.length} options for you.` : 'Based on your responses, we found the perfect card for you.'}
                    </div>
                </div>
                <div className='CardFinderResults__cardList'>
                    {cardFinderCards?.map((card: TCreditCard, index: number) => {
                        return (
                            <div key={index}>
                                <CardContainer bgColor='white' addDropShadow>
                                    <></>
                                    <div className='flex flex-row justify-between'>
                                        {width > 769 &&
                                        <>
                                            <div className='flex flex-col gap-6 w-[50%] md:w-full'>
                                                {cardHeading(index, card)}
                                                {cardDescription(card)}
                                            </div>
                                            <div className='CardFinderResults__card'>
                                                {cardImage(card)}
                                                {cardReviews(card)}
                                                {cardCTA(card)}
                                            </div>
                                        </>
                                        }
                                        {width <= 769 && 
                                        <div className='flex flex-col gap-6 w-50% md:w-full'>
                                            {cardHeading(index, card)}
                                            <div className='CardFinderResults__card'>
                                                {cardImage(card)}
                                                {cardReviews(card)}
                                            </div>
                                            {cardDescription(card)}
                                            {cardCTA(card)}
                                        </div>
                                        }
                                    </div>
                                </CardContainer>
                                {(index !== cardFinderCards.length - 1) && <div data-testid='spacer' className='h-4'/>}
                            </div>
                        );
                    })}
                </div>
            </div>
        </>);
};

export default CardFinderResults;
